export const PG_ROUTER_ADMIN_PATH = '/admin/pg-router';
export const DEFAULT_PG_ROUTER_ADMIN_FRAME_URL = 'http://localhost:5173/admin/pg-router';
export const PG_ROUTER_ADMIN_FRAME_AUTH_MODE = 'dashboard-shell';
export const PG_ROUTER_ADMIN_EMBEDDED_PARAM = 'embedded=true';
export const PG_ROUTER_AUTH_CONTEXT_MESSAGE = 'PG_ROUTER_AUTH_CONTEXT';
export const PG_ROUTER_AUTH_READY_MESSAGE = 'PG_ROUTER_AUTH_READY';
export const PG_ROUTER_AUTH_CONTEXT_RECEIVED_MESSAGE = 'PG_ROUTER_AUTH_CONTEXT_RECEIVED';
export const PG_ROUTER_ROUTE_CHANGE_MESSAGE = 'PG_ROUTER_ROUTE_CHANGE';
export const PG_ROUTER_FRAME_READY_TIMEOUT_MS = 45000;
export const PG_ROUTER_AUTH_CONTEXT_RETRY_INTERVAL_MS = 500;
export const PG_ROUTER_FRAME_SESSION_PARAM = 'frameSessionId';
export const PG_ROUTER_AUTH_PERMISSIONS = [
  'PAYPREDICT_CHECKER',
  'PAYPREDICT_MAKER',
  'OBSERVABILITY READ',
  'OBSERVABILITY UPDATE',
  'CREATE_ROUTES',
  'UPDATE_DELETE_CUSTOM_ROUTE_ADMIN',
  'UPDATE_DELETE_PREFERENCE_ROUTES',
  'UPDATE_PREFERENCE_PRIORITY_LOGIC',
  'UPDATE_DELETE_CUSTOM_ROUTING_LOGIC',
  'VIEW_GATEWAY_CONFIG',
  'UPDATE_GATEWAY_CONFIG',
  'CREATE_USER',
  'CREATE_ROLE',
  'CREATE_ADMIN_USER',
  'CREATE_ADMIN_ROLE',
  'UPDATE_DELETE_USER',
];
export const PG_ROUTER_ADMIN_SIDEBAR_PERMISSIONS = [
  'PAYPREDICT_CHECKER',
  'PAYPREDICT_MAKER',
  'OBSERVABILITY READ',
];

export const PG_ROUTER_ADMIN_LINKS = [
  { label: 'Dashboard', value: PG_ROUTER_ADMIN_PATH },
  { label: 'Approvals', value: `${PG_ROUTER_ADMIN_PATH}/approvals` },
  { label: 'Routing Rules', value: `${PG_ROUTER_ADMIN_PATH}/routing` },
  { label: 'Create Routing Rule', value: `${PG_ROUTER_ADMIN_PATH}/routing/create` },
  { label: 'Simulation', value: `${PG_ROUTER_ADMIN_PATH}/simulation` },
  { label: 'Add Merchant', value: `${PG_ROUTER_ADMIN_PATH}/merchants/add` },
  { label: 'Observability', value: `${PG_ROUTER_ADMIN_PATH}/observability` },
  { label: 'View Rules', value: `${PG_ROUTER_ADMIN_PATH}/rules` },
  { label: 'Create New Rule', value: `${PG_ROUTER_ADMIN_PATH}/rules/create` },
];

export type PgRouterAuthContext = {
  authToken: string;
  refreshToken: string;
  xsrfToken: string;
  tokenExpiry: string;
  userEmail: string;
  userId: string;
  userFullName: string;
  isAdmin: string;
  roleName: string;
  userPermissions: string[];
  paypredictRole: string;
  selectedMerchant: string;
  selectedMerchantId: string;
};

export type PgRouterFrameDiagnostic = {
  frameOrigin: string;
  framePath: string;
  dashboardPath: string;
  canShareAuthContext: boolean;
};

export type PgRouterAuthContextDiagnostic = {
  authContextPresent: boolean;
  userIdPresent: boolean;
  permissionCount: number;
};

export type PgRouterDiagnosticLevel = 'info' | 'warn' | 'error';

const REDACTED_DIAGNOSTIC_VALUE = '[redacted]';
const SENSITIVE_DIAGNOSTIC_KEY_PATTERN =
  /(token|authorization|cookie|email|fullname|fullName|merchant|permission)/i;

function truncateDiagnosticValue(value: string): string {
  return value.length > 180 ? `${value.slice(0, 177)}...` : value;
}

export function buildSafePgRouterDiagnosticPayload(
  frameDiagnostic: PgRouterFrameDiagnostic,
  authContextDiagnostic: PgRouterAuthContextDiagnostic,
  extra: Record<string, unknown> = {}
): Record<string, unknown> {
  return Object.entries({
    ...frameDiagnostic,
    ...authContextDiagnostic,
    ...extra,
  }).reduce((safePayload, [key, value]) => {
    if (SENSITIVE_DIAGNOSTIC_KEY_PATTERN.test(key)) {
      safePayload[key] = typeof value === 'boolean' || typeof value === 'number'
        ? value
        : REDACTED_DIAGNOSTIC_VALUE;
      return safePayload;
    }

    safePayload[key] = typeof value === 'string'
      ? truncateDiagnosticValue(value)
      : value;

    return safePayload;
  }, {} as Record<string, unknown>);
}

export function isPgRouterDiagnosticLoggingEnabled(
  env: Record<string, string | undefined> = process.env
): boolean {
  if (env.REACT_APP_ENABLE_PG_ROUTER_DIAGNOSTIC_LOGS === 'true') {
    return true;
  }

  if (env.REACT_APP_ENABLE_PG_ROUTER_DIAGNOSTIC_LOGS === 'false') {
    return false;
  }

  return env.NODE_ENV !== 'production';
}

export function logPgRouterAdminDiagnostic(
  level: PgRouterDiagnosticLevel,
  message: string,
  frameDiagnostic: PgRouterFrameDiagnostic,
  authContextDiagnostic: PgRouterAuthContextDiagnostic,
  extra: Record<string, unknown> = {}
): void {
  if (!isPgRouterDiagnosticLoggingEnabled()) {
    return;
  }

  const payload = buildSafePgRouterDiagnosticPayload(
    frameDiagnostic,
    authContextDiagnostic,
    extra
  );
  const eventPayload = {
    component: 'PgRouterAdmin',
    level,
    message,
    ...payload,
  };
  const rum = typeof window === 'undefined' ? undefined : (window as any).L9RUM;

  if (typeof rum?.addEvent === 'function') {
    rum.addEvent('pg_router_admin_diagnostic', eventPayload);
    return;
  }

  if (process.env.NODE_ENV !== 'production') {
    const logger = console[level] || console.log;
    logger(`[PG Router Admin] ${message}`, payload);
  }
}

export function hasPgRouterAdminSidebarPermission(
  permissionState: Record<string, any> = {}
): boolean {
  return PG_ROUTER_ADMIN_SIDEBAR_PERMISSIONS.some((permission) =>
    Boolean(permissionState?.[permission])
  );
}

function getDefaultPgRouterAdminFrameUrl(): string {
  return DEFAULT_PG_ROUTER_ADMIN_FRAME_URL;
}

function getPgRouterChildPath(currentDashboardPath = PG_ROUTER_ADMIN_PATH): string {
  const pathWithoutHash = currentDashboardPath.split('#')[0];
  const pathOnly = pathWithoutHash.split('?')[0];

  if (!pathOnly.startsWith(PG_ROUTER_ADMIN_PATH)) {
    return '';
  }

  return pathOnly.slice(PG_ROUTER_ADMIN_PATH.length);
}

function appendChildPath(url: string, childPath: string): string {
  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = withoutHash.indexOf('?');
  const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const query = queryIndex >= 0 ? withoutHash.slice(queryIndex) : '';
  const normalizedPath = path.replace(/\/+$/g, '');
  const normalizedChildPath = childPath.replace(/^\/+|\/+$/g, '');
  const pathWithChild = normalizedChildPath
    ? `${normalizedPath}/${normalizedChildPath}`
    : normalizedPath;

  return `${pathWithChild}${query}${hash}`;
}

function ensureTrailingSlash(url: string): string {
  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = withoutHash.indexOf('?');
  const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const query = queryIndex >= 0 ? withoutHash.slice(queryIndex) : '';
  const normalizedPath = path.endsWith('/') ? path : `${path}/`;

  return `${normalizedPath}${query}${hash}`;
}

function appendAuthMode(url: string): string {
  const normalizedUrl = ensureTrailingSlash(url);
  const [baseUrl, hash] = normalizedUrl.split('#');
  const urlWithAuthMode = /[?&]authMode=/.test(baseUrl)
    ? baseUrl
    : `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}authMode=${PG_ROUTER_ADMIN_FRAME_AUTH_MODE}`;
  const urlWithEmbeddedParam = /[?&]embedded=/.test(urlWithAuthMode)
    ? urlWithAuthMode
    : `${urlWithAuthMode}${urlWithAuthMode.includes('?') ? '&' : '?'}${PG_ROUTER_ADMIN_EMBEDDED_PARAM}`;

  return hash ? `${urlWithEmbeddedParam}#${hash}` : urlWithEmbeddedParam;
}

export function appendPgRouterFrameSessionId(url: string, frameSessionId = ''): string {
  if (!frameSessionId) {
    return url;
  }

  const [urlWithoutHash, hash] = url.split('#');
  const separator = urlWithoutHash.includes('?') ? '&' : '?';

  if (new RegExp(`[?&]${PG_ROUTER_FRAME_SESSION_PARAM}=`).test(urlWithoutHash)) {
    return hash ? `${urlWithoutHash}#${hash}` : urlWithoutHash;
  }

  const urlWithSession =
    `${urlWithoutHash}${separator}${PG_ROUTER_FRAME_SESSION_PARAM}=${encodeURIComponent(frameSessionId)}`;

  return hash ? `${urlWithSession}#${hash}` : urlWithSession;
}

export function normalizePgRouterDashboardPath(path = ''): string {
  const pathOnly = path.split('#')[0].split('?')[0];
  const normalizedPath = pathOnly.endsWith('/') && pathOnly !== '/'
    ? pathOnly.replace(/\/+$/g, '')
    : pathOnly;

  if (!normalizedPath.startsWith(PG_ROUTER_ADMIN_PATH)) {
    return '';
  }

  return normalizedPath || PG_ROUTER_ADMIN_PATH;
}

export function getActivePgRouterAdminLinkValue(
  path = '',
  links = PG_ROUTER_ADMIN_LINKS
): string {
  const normalizedPath = normalizePgRouterDashboardPath(path);

  if (!normalizedPath) {
    return '';
  }

  const exactMatch = links.find((link) => link.value === normalizedPath);
  if (exactMatch) {
    return exactMatch.value;
  }

  const nestedMatch = links
    .filter((link) => link.value !== PG_ROUTER_ADMIN_PATH)
    .filter((link) => normalizedPath.startsWith(`${link.value}/`))
    .sort((a, b) => b.value.length - a.value.length)[0];

  return nestedMatch?.value || '';
}

export function getPgRouterAdminFrameUrl(
  configuredUrl = process.env.REACT_APP_PG_ROUTER_ADMIN_URL,
  currentDashboardPath = PG_ROUTER_ADMIN_PATH
): string {
  const frameUrl = configuredUrl && configuredUrl.trim()
    ? configuredUrl.trim()
    : getDefaultPgRouterAdminFrameUrl();
  const childPath = getPgRouterChildPath(currentDashboardPath);

  return appendAuthMode(appendChildPath(frameUrl, childPath));
}

export function getPgRouterAdminFrameOrigin(frameUrl: string): string {
  const fallbackOrigin = typeof window === 'undefined'
    ? 'http://localhost'
    : window.location.origin;

  return new URL(frameUrl, fallbackOrigin).origin;
}

export function getPgRouterFrameDiagnostic(
  frameUrl: string,
  dashboardPath = PG_ROUTER_ADMIN_PATH,
  canShareAuthContext = false
): PgRouterFrameDiagnostic {
  const fallbackOrigin = typeof window === 'undefined'
    ? 'http://localhost'
    : window.location.origin;

  try {
    const parsedUrl = new URL(frameUrl, fallbackOrigin);

    return {
      frameOrigin: parsedUrl.origin,
      framePath: `${parsedUrl.pathname}${parsedUrl.search}`,
      dashboardPath,
      canShareAuthContext,
    };
  } catch (_err) {
    return {
      frameOrigin: 'invalid-frame-url',
      framePath: frameUrl,
      dashboardPath,
      canShareAuthContext,
    };
  }
}

function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function getConfiguredPgRouterAdminFrameOrigin(fallbackOrigin: string): string {
  const configuredUrl = process.env.REACT_APP_PG_ROUTER_ADMIN_URL;

  if (!configuredUrl) {
    return '';
  }

  try {
    return new URL(configuredUrl, fallbackOrigin).origin;
  } catch (_err) {
    return '';
  }
}

function isExplicitInsecurePgRouterFrameAllowed(
  parsedUrl: URL,
  fallbackOrigin: string
): boolean {
  if (process.env.REACT_APP_ALLOW_INSECURE_PG_ROUTER_ADMIN_URL !== 'true') {
    return false;
  }

  return parsedUrl.origin === getConfiguredPgRouterAdminFrameOrigin(fallbackOrigin);
}

export function isAllowedPgRouterAdminFrameUrl(
  frameUrl: string,
  dashboardHostname = typeof window === 'undefined'
    ? 'localhost'
    : window.location.hostname
): boolean {
  const fallbackOrigin = typeof window === 'undefined'
    ? 'http://localhost'
    : window.location.origin;

  try {
    const parsedUrl = new URL(frameUrl, fallbackOrigin);
    const configuredOrigin = getConfiguredPgRouterAdminFrameOrigin(fallbackOrigin);
    const isConfiguredHttpsFrame =
      parsedUrl.protocol === 'https:' &&
      Boolean(configuredOrigin) &&
      parsedUrl.origin === configuredOrigin;

    return isConfiguredHttpsFrame ||
      (isLocalHostname(parsedUrl.hostname) && isLocalHostname(dashboardHostname)) ||
      isExplicitInsecurePgRouterFrameAllowed(parsedUrl, fallbackOrigin);
  } catch (_err) {
    return false;
  }
}

function normalizeOptionalString(value: unknown): string {
  return value === undefined || value === null ? '' : String(value).trim();
}

function isSameNormalizedValue(left: string, right: string): boolean {
  return Boolean(left) && Boolean(right) && left.toLowerCase() === right.toLowerCase();
}

function getSelectedMerchantName(loginState: Record<string, any>, selectedMerchantId: string): string {
  if (!selectedMerchantId) {
    return '';
  }

  const userFullName = normalizeOptionalString(loginState.UserFullName);
  const candidateNames = [
    loginState.selectedMerchant?.merchantName,
    loginState.selectedMerchant?.merchantDisplayName,
  ].map(normalizeOptionalString);

  return candidateNames.find((merchantName) =>
    Boolean(merchantName) && !isSameNormalizedValue(merchantName, userFullName)
  ) || '';
}

export function buildPgRouterAuthContext(
  loginState: Record<string, any> = {},
  permissionState: Record<string, any> = {},
  roleState: Record<string, any> = {}
): PgRouterAuthContext | null {
  if (!loginState?.token) {
    return null;
  }

  const permissions = Object.entries(permissionState)
    .filter(([permission, enabled]) =>
      PG_ROUTER_AUTH_PERMISSIONS.includes(permission) && Boolean(enabled)
    )
    .map(([permission]) => permission);
  const paypredictRole = permissions.includes('PAYPREDICT_CHECKER')
    ? 'PAYPREDICT_CHECKER'
    : permissions.includes('PAYPREDICT_MAKER')
      ? 'PAYPREDICT_MAKER'
      : '';
  const selectedMerchantId = loginState.MerchantId ? String(loginState.MerchantId) : '';
  const selectedMerchantName = getSelectedMerchantName(loginState, selectedMerchantId);

  return {
    authToken: loginState.token,
    refreshToken: '',
    xsrfToken: '',
    tokenExpiry: loginState.tokenExpiry ? String(loginState.tokenExpiry) : '',
    userEmail: loginState.UserEmail || loginState.emailId || '',
    userId: loginState.userId ? String(loginState.userId) : '',
    userFullName: loginState.UserFullName || '',
    isAdmin: loginState.isAdmin ? 'true' : 'false',
    roleName: roleState.roleName || roleState.RoleName || roleState.name || '',
    userPermissions: permissions,
    paypredictRole,
    selectedMerchant: selectedMerchantName,
    selectedMerchantId,
  };
}
